import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Autocomplete,
	Button,
	Card,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	FormHelperText,
	IconButton,
	Link,
	Skeleton,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { LEADetails, PointOfContact } from '../../../../types/admin';
import { Check, Clear, Delete, List, Translate } from '@mui/icons-material';
import { InternalUserDetails } from '../../../../types/user';
import { callAPISync, getAllLEAs, getOrganisationUsers, updateOrganisationCustomData } from '../../../../utils/api';
import Logger from '../../../../utils/logger';
import { GetOrganisationUsersResponse } from '../../../../types/api';
import { sortRecord } from '../../../../utils/string';
import { useLogto } from '@logto/react';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';

const LEAsTab: React.FC = () => {
	const logto = useLogto();

	const [leas, setLEAs] = useState<LEADetails>({});
	const [expandedForce, setExpandedForce] = useState<string | null>(null);
	const [usersInOrg, setUsersInOrg] = useState<InternalUserDetails[]>([]);
	const [selectedUser, setSelectedUser] = useState<InternalUserDetails | null>(null);
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<boolean>(false);
	const [confirmationMessage, setConfirmationMessage] = useState<string>('');
	const [leaFilter, setLeaFilter] = useState<string>('');

	const onChange = (expanded: boolean, leaId: string) => {
		setUsersInOrg([]);
		setSelectedUser(null);
		setSubmitLoading(false);
		setSubmitError(false);
		setConfirmationMessage('');

		if (expanded) {
			callAPISync(logto, (token) => {
				getOrganisationUsers(token, leaId)
					.then((res) => res.json())
					.then((json) => json as GetOrganisationUsersResponse[])
					.then((resp) => {
						setUsersInOrg(
							resp.map((user) => {
								return {
									id: user.id,
									name: user.name,
									email: user.primaryEmail || 'Unknown',
									orgs: [],
									roles: [],
								};
							})
						);
					})
					.catch((e) => {
						Logger.warn(String(e));
					});
			});
			setExpandedForce(leaId);
		} else {
			setExpandedForce(null);
		}
	};

	const onAdd = (lea: string, user: InternalUserDetails) => {
		const currentLEA = leas[lea];

		setLEAs({
			...leas,
			[lea]: {
				...currentLEA,
				pointsOfContact: [
					...currentLEA.pointsOfContact,
					{
						userId: user.id,
						name: user.name,
						email: user.email,
					},
				],
			},
		});
		setSelectedUser(null);
	};

	const onRemove = (lea: string, user: PointOfContact) => {
		const currentLEA = { ...leas[lea] };

		currentLEA.pointsOfContact = currentLEA.pointsOfContact.filter((poc) => poc.userId !== user.userId);
		setLEAs({
			...leas,
			[lea]: {
				...currentLEA,
				pointsOfContact: [...currentLEA.pointsOfContact],
			},
		});
	};

	const onNotesChange = (lea: string, newNotes: string) => {
		setLEAs({
			...leas,
			[lea]: {
				...leas[lea],
				notes: newNotes,
			},
		});
	};

	const onOnboardedChange = (lea: string, onboarded: boolean) => {
		setLEAs({
			...leas,
			[lea]: {
				...leas[lea],
				onboarded: onboarded,
			},
		});
	};

	const onFeatureFlagChange = (_event: React.MouseEvent<HTMLElement>, lea: string, newFlags: string[]) => {
		setLEAs({
			...leas,
			[lea]: {
				...leas[lea],
				featureFlags: newFlags,
			},
		});
	};

	const onConfirm = (leaName: string, leaId: string) => {
		setConfirmationMessage('');
		setSubmitLoading(true);
		callAPISync(logto, (token) => {
			updateOrganisationCustomData(token, leaId, {
				pointsOfContact: leas[leaName].pointsOfContact.map((poc) => poc.userId),
				notes: leas[leaName].notes,
				onboarded: leas[leaName].onboarded,
				featureFlags: leas[leaName].featureFlags,
			})
				.then((res) => {
					if (res.ok) {
						setSubmitLoading(false);
						setConfirmationMessage(`Successfully updated ${leaName}`);
					} else {
						setSubmitLoading(false);
						setSubmitError(true);
						setConfirmationMessage(`Failed to update ${leaName} - ${res.status} ${res.statusText}`);
					}
				})
				.catch((e) => {
					setSubmitLoading(false);
					setSubmitError(true);
					setConfirmationMessage(`Failed to update ${leaName} - ${String(e)}`);
				});
		});
	};

	const filteredLEAs = Object.fromEntries(
		Object.entries(leas).filter(([key]) => key.toLocaleLowerCase().includes(leaFilter.trim().toLocaleLowerCase()))
	);

	useEffect(() => {
		callAPISync(logto, (token) => {
			getAllLEAs(token)
				.then((res) => res.json())
				.then((json) => setLEAs(sortRecord(json as LEADetails)))
				.catch((e) => {
					Logger.warn(String(e));
				});
		});
	}, []);

	return (
		<Stack width={'100%'} data-testid={'leas-tab-content'}>
			<Card variant="outlined" sx={{ p: 1, mb: 1 }}>
				<TextField
					fullWidth
					variant="outlined"
					label="Search by LEA Name"
					value={leaFilter}
					onChange={(e) => {
						setLeaFilter(e.target.value);
					}}
					slotProps={{
						input: {
							endAdornment: (
								<IconButton
									onClick={() => setLeaFilter('')}
									disabled={leaFilter === ''}
									data-testid={'clear-lea-search-button'}
									aria-label="Clear LEA Search Button"
								>
									<Clear />
								</IconButton>
							),
						},
					}}
					data-testid={'search-name-textfield'}
				/>
			</Card>

			{Object.keys(leas).length > 0 ? (
				Object.keys(filteredLEAs).length === 0 ? (
					<Alert severity="warning">No LEAs found</Alert>
				) : (
					Object.entries(filteredLEAs).map(([leaName, leaDetails]) => {
						return (
							<Accordion
								expanded={expandedForce === leaDetails.logtoId}
								onChange={(_, expanded) => onChange(expanded, leaDetails.logtoId)}
								key={`${leaName}-content`}
								slotProps={{
									heading: {
										component: 'div',
									},
								}}
								data-testid={`${leaName}-content`}
							>
								<AccordionSummary id={`${leaName}-accordion-summary`} aria-labelledby={`${leaName}-accordion-summary`}>
									<Typography variant="h6" component={'span'} fontWeight={'bold'} data-testid={`${leaName}-title`}>
										{leaName} ({leaDetails.numberOfUsers} Users)
									</Typography>
									<Chip
										color={leaDetails.onboarded ? 'success' : 'default'}
										sx={{ ml: 'auto' }}
										label={leaDetails.onboarded ? 'Onboarded' : 'Not Onboarded'}
									/>
								</AccordionSummary>
								<AccordionDetails>
									<Stack spacing={2}>
										<Typography>
											<b>Force Settings</b>
										</Typography>
										<ToggleButton
											data-testid={`${leaName}-onboarded-toggle`}
											sx={{ width: '20%' }}
											color="primary"
											value={`${leaName}-onboarded`}
											selected={leaDetails.onboarded}
											onChange={() => onOnboardedChange(leaName, !leaDetails.onboarded)}
										>
											Force Onboarded
											{leaDetails.onboarded && <Check sx={{ ml: 1 }} />}
										</ToggleButton>
										<Divider />
										<Typography>
											<b>Feature Flags</b>
										</Typography>
										<Alert severity="warning">
											These apply to every user in this LEA. Go to the LogTo admin console to change feature flags for individual users
										</Alert>
										<ToggleButtonGroup
											value={leaDetails.featureFlags}
											onChange={(_event, values) => onFeatureFlagChange(_event, leaName, values)}
										>
											<Tooltip title={`New Upload Form is ${leaDetails.featureFlags?.includes('newUploadForm') ? 'enabled' : 'disabled'}`}>
												<ToggleButton data-testid={`${leaName}-upload-feature-toggle`} value={`newUploadForm`} color="primary">
													<List sx={{ mr: 1 }} />
													New Upload Form
												</ToggleButton>
											</Tooltip>
											<Tooltip title={`Translation is ${leaDetails.featureFlags?.includes('translation') ? 'enabled' : 'disabled'}`}>
												<ToggleButton disabled value={`translation`} color="primary">
													<Translate sx={{ mr: 1 }} />
													Translation (not implemented)
												</ToggleButton>
											</Tooltip>
										</ToggleButtonGroup>
										<Divider />
										<Typography>
											<b>Email Domain:</b> {leaDetails.emailDomain}
										</Typography>
										<Divider />

										<Typography>
											<b>Enabled Use Cases</b>
										</Typography>

										<DataGrid
											rows={Object.entries(leaDetails.useCases).map(([name, useCaseDetails]) => ({
												name,
												...useCaseDetails,
											}))}
											columns={[
												{
													field: 'name',
													headerName: 'Use Case',
													flex: 1,
													display: 'flex',
													sortable: false,
													renderCell: (params: GridRenderCellParams) => <Typography>{params.row.name}</Typography>,
												},
												{
													field: 'rrd',
													headerName: 'RRD (days)',
													flex: 1,
													display: 'flex',
													sortable: false,
													renderCell: (params: GridRenderCellParams) => <Typography>{params.row.rrd}</Typography>,
												},
											]}
											hideFooter
											getRowId={(row) => row.name}
											disableColumnFilter
											localeText={{
												noRowsLabel: 'No use cases were found',
											}}
											sx={{
												'& .MuiDataGrid-row': {
													minHeight: '6ch !important',
												},
											}}
											getRowHeight={() => 'auto'}
											autoHeight
											disableRowSelectionOnClick
											disableColumnMenu
										/>

										<Divider />

										<Typography>
											<b>Points of Contact</b>
										</Typography>

										<DataGrid
											rows={leaDetails.pointsOfContact}
											columns={[
												{
													field: 'name',
													headerName: 'Name',
													flex: 1,
													display: 'flex',
													sortable: false,
													renderCell: (params: GridRenderCellParams) => (
														<Typography data-testid={`poc-${params.row.userId}-row`}>{params.row.name}</Typography>
													),
												},
												{
													field: 'email',
													headerName: 'Email',
													flex: 1,
													display: 'flex',
													sortable: false,
													renderCell: (params: GridRenderCellParams) => {
														return params.row.email === 'Unknown' || '' ? (
															<Typography data-testid={`poc-${params.row.userId}-email-cell`}>{'Unknown'}</Typography>
														) : (
															<Link data-testid={`poc-${params.row.userId}-email-cell`} href={`mailto:${params.row.email}`}>
																{params.row.email}
															</Link>
														);
													},
												},
												{
													field: 'remove',
													headerName: 'Remove',
													flex: 0.2,
													display: 'flex',
													sortable: false,
													headerAlign: 'center',
													align: 'center',
													renderCell: (params: GridRenderCellParams) => (
														<IconButton
															onClick={() => onRemove(leaName, params.row)}
															aria-label={`Remove Point of Contact ${params.row.name} from ${leaName}`}
															data-testid={`remove-${params.row.userId}-from-${leaName}-button`}
														>
															<Delete />
														</IconButton>
													),
												},
											]}
											hideFooter
											getRowId={(row) => row.userId}
											disableColumnFilter
											localeText={{
												noRowsLabel: 'No points of contact',
											}}
											sx={{
												'& .MuiDataGrid-row': {
													minHeight: '6ch !important',
												},
											}}
											getRowHeight={() => 'auto'}
											autoHeight
											disableRowSelectionOnClick
											disableColumnMenu
										/>

										{usersInOrg.length > 0 ? (
											<Stack direction={'row'} spacing={1}>
												<Autocomplete
													fullWidth
													value={selectedUser}
													onChange={(_, newUser) => setSelectedUser(newUser)}
													options={usersInOrg.filter((user) => !leaDetails.pointsOfContact.some((poc) => poc.userId === user.id))}
													renderInput={(params) => (
														<TextField
															{...params}
															slotProps={{
																htmlInput: { ...params.inputProps, 'aria-label': 'Select New Point of Contact' },
															}}
															label={'Select New Point of Contact'}
														/>
													)}
													getOptionLabel={(user) => `${user.name} (${user.email || 'Unknown'})`}
													data-testid={`${leaName}-poc-selector`}
												/>
												<Button
													variant="contained"
													disabled={!selectedUser}
													onClick={() => {
														if (selectedUser) {
															onAdd(leaName, selectedUser);
														}
													}}
													data-testid={`add-${leaName}-poc-button`}
												>
													Add
												</Button>
											</Stack>
										) : (
											<Box width={'100%'} display={'flex'} justifyItems={'center'}>
												<CircularProgress />
											</Box>
										)}
										<Divider />
										<TextField
											multiline
											value={leaDetails.notes}
											onChange={(e) => onNotesChange(leaName, e.target.value)}
											label={'Notes'}
											placeholder={`Enter any notes related to ${leaName}...`}
											rows={4}
											fullWidth
											helperText={'Maximum length of 1000 characters'}
											slotProps={{
												htmlInput: {
													maxLength: 1000,
												},
												inputLabel: {
													shrink: true,
												},
											}}
											data-testid={`${leaName}-notes-field`}
										/>
										<Collapse in={confirmationMessage !== ''}>
											<Alert
												variant="outlined"
												severity={submitError ? 'error' : 'success'}
												onClose={() => {
													setConfirmationMessage('');
													setSubmitError(false);
												}}
												data-testid={`${leaName}-confirmation-alert`}
											>
												{confirmationMessage}
											</Alert>
										</Collapse>

										<Button
											variant="contained"
											sx={{ padding: 1 }}
											onClick={() => onConfirm(leaName, leaDetails.logtoId)}
											data-testid={`confirm-${leaName}-changes-button`}
											disabled={submitLoading}
										>
											{submitLoading ? 'Submitting...' : 'Confirm Updates'}
										</Button>
										<FormHelperText>Any changes made will not take effect until confirmed</FormHelperText>
									</Stack>
								</AccordionDetails>
							</Accordion>
						);
					})
				)
			) : (
				<Stack spacing={1}>
					{' '}
					<Skeleton variant="rounded" height={60} /> <Skeleton variant="rounded" height={60} /> <Skeleton variant="rounded" height={60} />
				</Stack>
			)}
		</Stack>
	);
};

export default LEAsTab;
